import React, { useEffect, useState } from "react";
import axios from "axios";
import { getBaseUrl, loadStorage } from "../../Utils/Utils";

const STATES = ["Delhi", "Uttar Pradesh", "Haryana", "Punjab", "Himachal Pradesh", "Rajasthan", "Uttarakhand"];
const empty = { Pincode: '', City: '', State: '' };

function ManagePincodes() {
    const [pincodes, setPincodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(empty);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [filterState, setFilterState] = useState('');

    const headers = () => ({ Authorization: `Bearer ${loadStorage("token")}` });

    const fetchPincodes = () =>
        axios.get(`${getBaseUrl()}/pincode`, { headers: headers() })
            .then(res => setPincodes(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchPincodes(); }, []);

    const openEdit = (row) => { setForm({ Pincode: row.pincode, City: row.city, State: row.state }); setEditId(row.id); setError(''); };
    const cancelEdit = () => { setForm(empty); setEditId(null); setError(''); };

    const save = async (e) => {
        e.preventDefault();
        if (!/^\d{6}$/.test(form.Pincode.trim())) { setError('Pincode must be exactly 6 digits.'); return; }
        if (!form.City.trim() || !form.State) { setError('City and State are required.'); return; }
        setSaving(true); setError('');
        try {
            if (editId) {
                await axios.put(`${getBaseUrl()}/pincode/${editId}`, form, { headers: headers() });
            } else {
                await axios.post(`${getBaseUrl()}/pincode`, form, { headers: headers() });
            }
            await fetchPincodes();
            setForm(empty); setEditId(null);
        } catch (err) {
            setError(err.response?.data || 'Failed to save pincode.');
        } finally { setSaving(false); }
    };

    const remove = async (id, pincode) => {
        if (!window.confirm(`Remove pincode ${pincode}?`)) return;
        await axios.delete(`${getBaseUrl()}/pincode/${id}`, { headers: headers() });
        setPincodes(prev => prev.filter(p => p.id !== id));
    };

    const filtered = pincodes.filter(p =>
        (!search || p.pincode.includes(search) || p.city.toLowerCase().includes(search.toLowerCase())) &&
        (!filterState || p.state === filterState)
    );

    const groupedCount = STATES.reduce((acc, s) => {
        acc[s] = pincodes.filter(p => p.state === s).length;
        return acc;
    }, {});

    return (
        <div className="col-lg-8 col-md-8 col-12">
            <div className="ml-0 ml-md-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 text-white">Manage Delivery Pincodes</h5>
                    <span className="badge bg-primary">{pincodes.length} total</span>
                </div>

                {/* State summary chips */}
                <div className="mb-3 d-flex flex-wrap gap-2">
                    {STATES.map(s => (
                        <span
                            key={s}
                            className={`badge cursor-pointer ${filterState === s ? 'bg-primary' : 'bg-secondary'}`}
                            style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                            onClick={() => setFilterState(prev => prev === s ? '' : s)}
                        >
                            {s} ({groupedCount[s] || 0})
                        </span>
                    ))}
                </div>

                {/* Add / Edit form */}
                <div className="card mb-3">
                    <div className="card-body">
                        <h6 className="mb-3">{editId ? 'Edit Pincode' : 'Add New Pincode'}</h6>
                        {error && <div className="alert alert-danger py-2 small">{error}</div>}
                        <form onSubmit={save}>
                            <div className="row g-2">
                                <div className="col-sm-3">
                                    <input className="form-control form-control-sm" placeholder="Pincode *" maxLength={6}
                                        value={form.Pincode} onChange={e => setForm(f => ({ ...f, Pincode: e.target.value.replace(/\D/g, '') }))} />
                                </div>
                                <div className="col-sm-4">
                                    <input className="form-control form-control-sm" placeholder="City *"
                                        value={form.City} onChange={e => setForm(f => ({ ...f, City: e.target.value }))} />
                                </div>
                                <div className="col-sm-3">
                                    <select className="form-control form-control-sm" value={form.State}
                                        onChange={e => setForm(f => ({ ...f, State: e.target.value }))}>
                                        <option value="">State *</option>
                                        {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="col-sm-2 d-flex gap-1">
                                    <button className="btn btn-primary btn-sm flex-grow-1" type="submit" disabled={saving}>
                                        {saving ? '...' : editId ? 'Update' : 'Add'}
                                    </button>
                                    {editId && (
                                        <button className="btn btn-secondary btn-sm" type="button" onClick={cancelEdit}>✕</button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Search */}
                <div className="mb-2">
                    <input className="form-control form-control-sm" placeholder="Search by pincode or city..."
                        value={search} onChange={e => setSearch(e.target.value)} />
                </div>

                {/* Pincodes table */}
                {loading ? (
                    <p className="text-white">Loading...</p>
                ) : (
                    <div className="card">
                        <div className="table-responsive">
                            <table className="table table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th>Pincode</th>
                                        <th>City</th>
                                        <th>State</th>
                                        <th style={{ width: 80 }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr><td colSpan={4} className="text-center text-muted py-3">No pincodes found.</td></tr>
                                    ) : filtered.map(row => (
                                        <tr key={row.id} className={editId === row.id ? 'table-active' : ''}>
                                            <td className="py-2 font-weight-bold">{row.pincode}</td>
                                            <td className="py-2">{row.city}</td>
                                            <td className="py-2"><span className="badge bg-secondary">{row.state}</span></td>
                                            <td className="py-2">
                                                <div className="d-flex gap-1">
                                                    <button className="btn btn-xs btn-outline-primary" style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                                                        onClick={() => openEdit(row)}>Edit</button>
                                                    <button className="btn btn-xs btn-outline-danger" style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                                                        onClick={() => remove(row.id, row.pincode)}>Del</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="card-footer small text-muted">
                            Showing {filtered.length} of {pincodes.length} pincodes
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ManagePincodes;
